export const RESIDENCY_OPTIONS = [
    { id: 'uae_national', label: 'UAE National', minDownPaymentPercent: 15 },
    { id: 'uae_resident', label: 'UAE Resident', minDownPaymentPercent: 20 },
    { id: 'non_resident', label: 'Non-Resident', minDownPaymentPercent: 40 },
]

export const DEFAULTS = {
    propertyValue: 300000,
    interestRate: 3.75,
    loanYears: 25,

    propertyMin: 300000,
    propertyMax: 200000000,

    interestMin: 1,
    interestMax: 10,

    loanYearsMin: 1,
    loanYearsMax: 25,
}

export const formatAED = (value, fractionDigits = 0) => {
    const num = Number(value) || 0
    return new Intl.NumberFormat('en-AE', {
        style: 'decimal',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(num)
}

export const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

/** Standard amortizing monthly payment */
export const calculateMonthlyPayment = (loanAmount, annualInterestRate, loanYears) => {
    const principal = Number(loanAmount) || 0
    const years = Number(loanYears) || 0
    const annualRate = Number(annualInterestRate) || 0

    if (principal <= 0 || years <= 0) return 0

    const monthlyRate = annualRate / 100 / 12
    const months = years * 12

    if (monthlyRate === 0) return principal / months

    const factor = Math.pow(1 + monthlyRate, months)
    return (principal * monthlyRate * factor) / (factor - 1)
}

/** First-month principal / interest split */
export const calculateFirstMonthBreakdown = (loanAmount, annualInterestRate, loanYears) => {
    const monthlyPayment = calculateMonthlyPayment(loanAmount, annualInterestRate, loanYears)
    const monthlyRate = (Number(annualInterestRate) || 0) / 100 / 12
    const interestPortion = (Number(loanAmount) || 0) * monthlyRate
    const principalPortion = Math.max(monthlyPayment - interestPortion, 0)
    return { monthlyPayment, principalPortion, interestPortion }
}

/** Total interest paid over full term */
export const calculateTotalInterest = (loanAmount, annualInterestRate, loanYears) => {
    const monthly = calculateMonthlyPayment(loanAmount, annualInterestRate, loanYears)
    const months = (Number(loanYears) || 0) * 12
    if (months <= 0) return 0
    return Math.max(monthly * months - (Number(loanAmount) || 0), 0)
}

/**
 * UAE property purchase fees (estimates aligned with market calculators)
 * @see yallacompare, mortgagefinder upfront cost breakdowns
 */
export const calculateUpfrontFees = (propertyValue, loanAmount) => {
    const property = Number(propertyValue) || 0
    const loan = Number(loanAmount) || 0

    const dldFee = property * 0.04
    const mortgageRegistration = loan * 0.0025 + 290
    const mortgageProcessing = loan * 0.01
    const valuation = 3000
    const brokerCommission = property * 0.02 * 1.05
    const conveyance = 5000
    const registrationTrustee = property > 500000 ? 4200 : 2100
    const nocFee = 1000

    const fees = {
        dldFee,
        mortgageRegistration,
        mortgageProcessing,
        valuation,
        brokerCommission,
        conveyance,
        registrationTrustee,
        nocFee,
    }

    const totalFees = Object.values(fees).reduce((sum, v) => sum + v, 0)

    return { ...fees, totalFees }
}

export const computeMortgage = ({
    propertyValue,
    downPaymentPercent,
    interestRate,
    loanYears,
}) => {
    const property = Number(propertyValue) || 0
    const downPct = Number(downPaymentPercent) || 0
    const downPayment = (property * downPct) / 100
    const loanAmount = Math.max(property - downPayment, 0)
    const loanPercent = property > 0 ? (loanAmount / property) * 100 : 0

    const monthlyPayment = calculateMonthlyPayment(loanAmount, interestRate, loanYears)
    const { principalPortion, interestPortion } = calculateFirstMonthBreakdown(
        loanAmount,
        interestRate,
        loanYears
    )
    const totalInterest = calculateTotalInterest(loanAmount, interestRate, loanYears)
    const upfrontFees = calculateUpfrontFees(property, loanAmount)
    const requiredUpfront = downPayment + upfrontFees.totalFees

    return {
        downPayment,
        loanAmount,
        loanPercent,
        downPaymentPercent: downPct,
        monthlyPayment,
        principalPortion,
        interestPortion,
        totalInterest,
        upfrontFees,
        requiredUpfront,
    }
}
